import { ReactNode, useState, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuthStore } from '../store/auth';
import { useEntitlements } from '../hooks/useEntitlements';

interface LayoutProps {
    children: ReactNode;
}

const menuSections = [
    {
        label: 'Chính',
        items: [
            { path: '/dashboard', label: 'Tổng quan', icon: '📊' },
        ],
    },
    {
        label: 'Chiến dịch',
        items: [
            { path: '/campaigns', label: 'Chiến dịch', icon: '📣' },
            { path: '/gifts', label: 'Quà tặng', icon: '🎁' },
        ],
    },
    {
        label: 'Thư viện',
        items: [
            { path: '/game-templates', label: 'Kho trò chơi', icon: '📦' },
        ],
    },
    {
        label: 'Phân tích',
        items: [
            { path: '/players', label: 'Người chơi', icon: '👥' },
            { path: '/reports', label: 'Báo cáo', icon: '📈' },
        ],
    },
    {
        label: 'Quản trị',
        items: [
            { path: '/billing', label: 'Thanh toán', icon: '💳' },
            { path: '/users', label: 'Thành viên', icon: '👤' },
            { path: '/audit-logs', label: 'Nhật ký', icon: '📋' },
            { path: '/settings', label: 'Cài đặt', icon: '⚙️' },
            { path: '/system', label: 'Hệ thống', icon: '🛠️' },
            { path: '/system/moderation', label: 'Kiểm duyệt', icon: '🛡️' },
        ],
    },
];

const planStyles: Record<string, { bg: string; text: string; label: string; icon: string }> = {
    free: { bg: 'bg-gray-100 text-gray-600 border border-gray-200', text: 'Free', label: 'Dùng thử', icon: '🔓' },
    flash_3d: { bg: 'bg-teal-50 text-teal-700 border border-teal-200', text: 'Flash 3D', label: 'Flash 3 ngày', icon: '⚡' },
    mini_7d: { bg: 'bg-green-50 text-green-700 border border-green-200', text: 'Mini 7D', label: 'Mini 7 ngày', icon: '🌟' },
    event_15d: { bg: 'bg-orange-50 text-orange-700 border border-orange-200 shadow-sm', text: 'Event VIP', label: 'Sự kiện 15N', icon: '🔥' },
    brand_30d: { bg: 'bg-blue-50 text-blue-700 border border-blue-200 shadow-sm font-semibold', text: 'Brand VIP', label: 'Thương hiệu', icon: '💎' },
    agency_30d: { bg: 'bg-gradient-to-r from-purple-500 to-indigo-600 text-white border-transparent shadow-sm font-bold', text: 'Agency Max', label: 'Agency', icon: '👑' },
};

export default function Layout({ children }: LayoutProps) {
    const location = useLocation();
    const navigate = useNavigate();
    const { logout, user } = useAuthStore();
    const { entitlements, loading } = useEntitlements();

    const [walletBalance, setWalletBalance] = useState<number | null>(null);

    const isSuperAdmin = user?.roles?.includes('super_admin');
    const impersonated = localStorage.getItem('impersonated_tenant_id');
    const isTenantSpecificPage = ['/dashboard', '/campaigns', '/gifts', '/players', '/reports', '/billing', '/users', '/audit-logs', '/settings'].some(
        path => location.pathname === path || location.pathname.startsWith(path + '/')
    );
    const needImpersonation = isSuperAdmin && !impersonated && isTenantSpecificPage;

    useEffect(() => {
        const fetchWallet = async () => {
            try {
                const token = localStorage.getItem('auth_token');
                if (!token) return;
                const headers: Record<string, string> = {
                    'Authorization': `Bearer ${token}`,
                    'Accept': 'application/json',
                };
                const impersonated = localStorage.getItem('impersonated_tenant_id');
                if (impersonated) {
                    headers['X-Tenant-ID'] = impersonated;
                }
                const API = import.meta.env.VITE_API_URL || '/api';
                const res = await fetch(`${API}/admin/wallet`, { headers });
                const json = await res.json();
                if (json?.data?.balance !== undefined) {
                    setWalletBalance(json.data.balance);
                }
            } catch (err) {
                console.error('Error fetching wallet balance in layout:', err);
            }
        };
        fetchWallet();
    }, [location.pathname]);

    const handleLogout = () => {
        logout();
        navigate('/login');
    };

    // Get active plan style or fallback
    const currentPlanCode = entitlements?.plan_code ?? 'free';
    const planStyle = planStyles[currentPlanCode] || planStyles.free;

    const isTenantAdmin = user?.roles?.includes('tenant_admin');

    // Filter sections based on roles and plan cước
    const visibleSections = menuSections.map(section => {
        const filteredItems = section.items.filter(item => {
            if (item.path === '/system') {
                return isSuperAdmin;
            }
            if (item.path === '/game-templates') {
                return true; // Cho phép tất cả người dùng xem Kho trò chơi
            }
            if (item.path === '/settings') {
                return isSuperAdmin || isTenantAdmin;
            }
            if (item.path === '/users' || item.path === '/audit-logs') {
                if (!isSuperAdmin && !isTenantAdmin) return false;
            }
            // Ẩn các chức năng cao cấp đối với gói Free (nếu không phải super_admin)
            if (currentPlanCode === 'free' && !isSuperAdmin) {
                const restrictedPaths = ['/players', '/reports', '/users', '/audit-logs'];
                if (restrictedPaths.includes(item.path)) {
                    return false;
                }
            }
            return true;
        });
        return { ...section, items: filteredItems };
    }).filter(section => section.items.length > 0);

    // Giới hạn theo Gói cước (Free)
    const planRestrictedPaths = ['/players', '/reports', '/users', '/audit-logs'];
    const isPlanRestricted = !isSuperAdmin && (currentPlanCode === 'free' && planRestrictedPaths.some(path => location.pathname === path || location.pathname.startsWith(path)));

    // Giới hạn theo Vai trò (Chỉ Admin/Super Admin được vào)
    const adminOnlyPaths = ['/users', '/audit-logs', '/settings'];
    const isRoleRestricted = !isSuperAdmin && adminOnlyPaths.some(path => location.pathname === path || location.pathname.startsWith(path)) && !isTenantAdmin;

    // Giới hạn dành riêng cho Super Admin hệ thống
    const superAdminOnlyPaths = ['/system', '/system/moderation'];
    const isSuperAdminRestricted = !isSuperAdmin && superAdminOnlyPaths.some(path => location.pathname === path || location.pathname.startsWith(path));

    const isRestricted = isPlanRestricted || isRoleRestricted || isSuperAdminRestricted;

    const getPageLabel = () => {
        if (location.pathname.startsWith('/game-templates')) return 'Thư viện & Kho trò chơi';
        if (location.pathname.startsWith('/players')) return 'Quản lý người chơi';
        if (location.pathname.startsWith('/reports')) return 'Báo cáo & Phân tích';
        if (location.pathname.startsWith('/users')) return 'Quản lý thành viên';
        if (location.pathname.startsWith('/audit-logs')) return 'Nhật ký hoạt động';
        if (location.pathname.startsWith('/settings')) return 'Cài đặt cửa hàng';
        return 'Tính năng nâng cao';
    };

    return (
        <div className="min-h-screen bg-gray-100">
            <aside className="fixed left-0 top-0 h-full w-64 border-r border-gray-200 bg-white shadow-sm flex flex-col justify-between">
                <div>
                    <div className="border-b border-gray-200 p-4">
                        <h1 className="text-xl font-bold text-blue-600">X_Game Admin</h1>
                        
                        {user?.full_name && (
                            <div className="mt-2">
                                <p className="text-sm font-semibold text-gray-800 leading-tight">{user.full_name}</p>
                                <p className="text-[10px] text-gray-400 mt-0.5 font-mono truncate">{user.email}</p>
                                
                                <div className="mt-2.5 flex flex-wrap items-center gap-1.5">
                                    {isSuperAdmin ? (
                                        <div className="flex flex-col gap-1.5 w-full">
                                            <span className="inline-flex items-center gap-1 text-[10px] font-extrabold uppercase px-2.5 py-1 rounded-full bg-gradient-to-r from-rose-500 to-red-600 text-white shadow-sm border border-transparent w-fit">
                                                👑 Super Admin
                                            </span>
                                            {localStorage.getItem('impersonated_tenant_id') && (
                                                <span className={`inline-flex items-center gap-1 text-[10px] font-bold uppercase px-2 py-0.5 rounded-md ${planStyle.bg} w-fit`} title={`Giả lập gói: ${planStyle.label}`}>
                                                    <span>{planStyle.icon}</span> {planStyle.text} (Đóng vai)
                                                </span>
                                            )}
                                        </div>
                                    ) : (
                                        <>
                                            <span className={`inline-flex items-center gap-1 text-[10px] font-bold uppercase px-2 py-0.5 rounded-md ${planStyle.bg}`} title={`Gói cước: ${planStyle.label}`}>
                                                <span>{planStyle.icon}</span> {planStyle.text}
                                            </span>
                                            {currentPlanCode === 'free' && (
                                                <Link 
                                                    to="/billing" 
                                                    className="text-[10px] font-bold text-orange-500 hover:text-orange-600 transition-colors flex items-center gap-0.5 hover:underline"
                                                >
                                                    👑 Nâng cấp
                                                </Link>
                                            )}
                                        </>
                                    )}

                                    {/* Ví xu hiển thị toàn cục cho cả Admin và User */}
                                    <Link 
                                        to="/billing" 
                                        className="inline-flex items-center gap-1 text-[10px] font-black uppercase px-2 py-0.5 rounded-md bg-amber-50 text-amber-700 border border-amber-200 hover:bg-amber-100 hover:border-amber-300 transition-all shadow-sm w-fit"
                                        title="Ví xu của bạn - Click để nạp hoặc đổi gói"
                                    >
                                        <span>🪙</span> {walletBalance !== null ? `${walletBalance.toLocaleString('vi-VN')} xu` : '...'}
                                    </Link>
                                </div>
                            </div>
                        )}
                    </div>
                    
                    <nav className="space-y-4 p-4 max-h-[calc(100vh-170px)] overflow-y-auto">
                        {visibleSections.map((section) => (
                            <div key={section.label}>
                                <div className="px-3 text-[11px] font-semibold uppercase tracking-wider text-gray-400">
                                    {section.label}
                                </div>
                                <div className="mt-1.5 space-y-0.5">
                                    {section.items.map((item) => {
                                        const isActive = location.pathname === item.path
                                            || (item.path !== '/dashboard' && location.pathname.startsWith(item.path));

                                        return (
                                            <Link
                                                key={item.path}
                                                to={item.path}
                                                className={`flex items-center gap-3 rounded-xl px-3 py-2 text-sm font-medium transition-colors ${isActive
                                                    ? 'bg-blue-50 text-blue-600'
                                                    : 'text-gray-700 hover:bg-gray-50'
                                                    }`}
                                            >
                                                <span className="w-5 text-center text-xs leading-5 font-semibold">
                                                    {item.icon}
                                                </span>
                                                {item.label}
                                            </Link>
                                        );
                                    })}
                                </div>
                            </div>
                        ))}
                    </nav>
                </div>
                
                <div className="border-t border-gray-200 p-4 bg-white">
                    <button
                        onClick={handleLogout}
                        className="w-full flex items-center justify-center gap-2 rounded-xl px-3 py-2 text-sm font-semibold text-red-600 transition-colors border border-red-100 hover:bg-red-50"
                    >
                        <span>🚪</span> Đăng xuất
                    </button>
                </div>
            </aside>

            <main className="ml-64 min-h-screen">
                {localStorage.getItem('impersonated_tenant_id') && (
                    <div className="bg-gradient-to-r from-amber-500 via-orange-500 to-orange-600 text-white px-6 py-3 flex items-center justify-between shadow-md select-none">
                        <div className="flex items-center gap-2 text-sm font-semibold">
                            <span className="text-lg">👤</span>
                            <span>
                                <strong>Chế độ đóng vai:</strong> Bạn đang quản lý cửa hàng <strong>{localStorage.getItem('impersonated_tenant_name') || localStorage.getItem('impersonated_tenant_id')}</strong>
                            </span>
                        </div>
                        <button
                            onClick={() => {
                                localStorage.removeItem('impersonated_tenant_id');
                                localStorage.removeItem('impersonated_tenant_name');
                                window.location.href = '/system';
                            }}
                            className="bg-white/20 hover:bg-white/30 text-white font-bold text-xs px-3.5 py-1.5 rounded-lg transition-all border border-white/20 hover:scale-105 active:scale-95"
                        >
                            🛑 Thoát đóng vai
                        </button>
                    </div>
                )}
                <div className="p-8">
                    {isRestricted ? (
                        <div className="max-w-2xl mx-auto mt-16 bg-white border border-rose-100 rounded-3xl p-8 shadow-xl text-center relative overflow-hidden">
                            {/* Decorative background shape */}
                            <div className="absolute top-0 right-0 w-32 h-32 bg-rose-50 rounded-full blur-3xl -z-10" />
                            <div className="absolute bottom-0 left-0 w-32 h-32 bg-blue-50 rounded-full blur-3xl -z-10" />
                            
                            {isPlanRestricted ? (
                                <>
                                    <span className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-orange-50 text-orange-500 text-3xl mb-6 shadow-sm border border-orange-100 animate-pulse">
                                        🔒
                                    </span>
                                    <h2 className="text-2xl font-bold text-gray-900 mb-2">Tính năng này đã bị khóa</h2>
                                    <p className="text-gray-500 mb-6 leading-relaxed max-w-md mx-auto">
                                        Trang <strong>{getPageLabel()}</strong> chỉ dành cho các tài khoản sử dụng gói dịch vụ nâng cao. Vui lòng nâng cấp gói cước để mở khóa toàn bộ các tính năng đặc quyền.
                                    </p>
                                    <div className="flex items-center justify-center gap-4">
                                        <button 
                                            onClick={() => navigate('/dashboard')} 
                                            className="px-5 py-2.5 rounded-xl border border-gray-200 text-sm font-semibold text-gray-600 hover:bg-gray-50 transition-colors"
                                        >
                                            Quay lại Dashboard
                                        </button>
                                        <button 
                                            onClick={() => navigate('/billing')} 
                                            className="px-6 py-2.5 rounded-xl bg-gradient-to-r from-orange-500 to-amber-500 text-sm font-bold text-white shadow-md hover:shadow-lg transition-all hover:-translate-y-0.5"
                                        >
                                            👑 Nâng cấp ngay
                                        </button>
                                    </div>
                                </>
                            ) : (
                                <>
                                    <span className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-red-50 text-red-500 text-3xl mb-6 shadow-sm border border-red-100 animate-pulse">
                                        🚫
                                    </span>
                                    <h2 className="text-2xl font-bold text-gray-900 mb-2">Quyền truy cập bị từ chối</h2>
                                    <p className="text-gray-500 mb-6 leading-relaxed max-w-md mx-auto">
                                        Bạn không có quyền truy cập vào trang <strong>{getPageLabel()}</strong>. Tính năng này yêu cầu quyền quản trị viên cấp cao hơn. Vui lòng liên hệ với Quản trị viên của cửa hàng.
                                    </p>
                                    <div className="flex items-center justify-center">
                                        <button 
                                            onClick={() => navigate('/dashboard')} 
                                            className="px-6 py-2.5 rounded-xl bg-gradient-to-r from-rose-500 to-red-500 text-sm font-bold text-white shadow-md hover:shadow-lg transition-all hover:-translate-y-0.5"
                                        >
                                            Quay lại Dashboard
                                        </button>
                                    </div>
                                </>
                            )}
                        </div>
                    ) : needImpersonation ? (
                        <WorkspaceSelector />
                    ) : children}
                </div>
            </main>
        </div>
    );
}

function WorkspaceSelector() {
    const [tenants, setTenants] = useState<any[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [search, setSearch] = useState('');

    useEffect(() => {
        const fetchTenants = async () => {
            try {
                const token = localStorage.getItem('auth_token');
                const API = import.meta.env.VITE_API_URL || '/api';
                const url = search 
                    ? `${API}/super/tenants?search=${encodeURIComponent(search)}`
                    : `${API}/super/tenants`;
                const res = await fetch(url, {
                    headers: {
                        'Authorization': `Bearer ${token}`,
                        'Accept': 'application/json',
                    }
                });
                const data = await res.json();
                if (data.success) {
                    setTenants(data.data);
                } else {
                    setError(data.message || 'Không thể tải danh sách cửa hàng');
                }
            } catch (err) {
                setError('Có lỗi xảy ra khi tải danh sách cửa hàng.');
            } finally {
                setLoading(false);
            }
        };
        fetchTenants();
    }, [search]);

    const handleImpersonate = (tenant: any) => {
        localStorage.setItem("impersonated_tenant_id", tenant.id);
        localStorage.setItem("impersonated_tenant_name", tenant.name);
        window.location.reload();
    };

    return (
        <div className="max-w-4xl mx-auto mt-8 bg-white border border-gray-200 rounded-3xl p-8 shadow-xl">
            <div className="text-center mb-8">
                <span className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-amber-50 text-amber-500 text-3xl mb-4 border border-amber-100 shadow-sm animate-bounce">
                    👤
                </span>
                <h2 className="text-2xl font-bold text-gray-900 mb-2">Đóng vai Cửa hàng (Workspace)</h2>
                <p className="text-gray-500 max-w-md mx-auto text-sm">
                    Tài khoản của bạn là <strong>Super Admin</strong> hệ thống. Để xem và quản lý dữ liệu chiến dịch, quà tặng, lượt chơi hoặc báo cáo, vui lòng chọn đóng vai một cửa hàng dưới đây.
                </p>
            </div>

            <div className="mb-6 flex gap-2">
                <div className="relative flex-1">
                    <input
                        type="text"
                        placeholder="Tìm kiếm cửa hàng theo tên, email..."
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                        className="w-full px-4 py-2.5 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-amber-500 text-sm"
                    />
                </div>
            </div>

            {loading ? (
                <div className="flex h-48 items-center justify-center">
                    <div className="text-gray-500 text-sm">Đang tải danh sách cửa hàng...</div>
                </div>
            ) : error ? (
                <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-xl text-sm mb-4">
                    {error}
                </div>
            ) : tenants.length === 0 ? (
                <div className="text-center py-12 text-gray-500 text-sm border border-dashed border-gray-200 rounded-2xl">
                    Không tìm thấy cửa hàng nào.
                </div>
            ) : (
                <div className="overflow-hidden border border-gray-200 rounded-2xl animate-fade-in">
                    <table className="min-w-full divide-y divide-gray-200">
                        <thead className="bg-gray-50">
                            <tr>
                                <th className="px-6 py-3 text-left text-xs font-semibold uppercase tracking-wider text-gray-500">Cửa hàng</th>
                                <th className="px-6 py-3 text-left text-xs font-semibold uppercase tracking-wider text-gray-500">Mã</th>
                                <th className="px-6 py-3 text-left text-xs font-semibold uppercase tracking-wider text-gray-500">Gói dịch vụ</th>
                                <th className="px-6 py-3 text-center text-xs font-semibold uppercase tracking-wider text-gray-500">Hành động</th>
                            </tr>
                        </thead>
                        <tbody className="bg-white divide-y divide-gray-200 text-sm">
                            {tenants.map((tenant) => (
                                <tr key={tenant.id} className="hover:bg-gray-50">
                                    <td className="px-6 py-4">
                                        <div className="font-semibold text-gray-900">{tenant.name}</div>
                                        <div className="text-xs text-gray-400">{tenant.owner_email || 'Không có email'}</div>
                                    </td>
                                    <td className="px-6 py-4 font-mono text-xs text-gray-600">{tenant.code}</td>
                                    <td className="px-6 py-4">
                                        <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-semibold bg-blue-50 text-blue-700 border border-blue-100">
                                            {tenant.plan_code || 'free'}
                                        </span>
                                    </td>
                                    <td className="px-6 py-4 text-center">
                                        <button
                                            onClick={() => handleImpersonate(tenant)}
                                            className="px-4 py-2 bg-gradient-to-r from-amber-500 to-orange-500 hover:from-amber-600 hover:to-orange-600 text-white rounded-xl text-xs font-bold transition-all shadow-sm active:scale-95 cursor-pointer"
                                        >
                                            👤 Đóng vai
                                        </button>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}
        </div>
    );
}
